import { Col, Row, Typography, Image, Flex, Dropdown, App } from 'antd'
import React, { memo } from 'react'
import { DownOutlined } from '@ant-design/icons'
import CustomButton from '../../../components/CustomButton/Button'
import { HorizotalSteps } from '../../../components/CustomSteps/Steps';
import { calculateReturns, formatCurrency, downloadBlob, getDownloadErrorMessage, getDownloadSuccessMessage, validateDownloadBlob } from '../../../helpers/helpers';
import axios from "../../../config/httpConfig";
const { Text } = Typography;

const DOWNLOAD_LABELS = {
    dealAgreement: 'Deal Agreement',
    note: 'Transaction Note',
    invoice: 'Transaction Invoice',
    repay: 'Repayment Status',
    all: 'Download All',
};

const OrderCard = ({ order, selectedTab }) => {
    const { message } = App.useApp();
    const isOngoing = selectedTab === "ongoing";

    const handleDownload = async (type) => {
        try {
            let blob;
            let filename;

            if (type === 'dealAgreement') {
                blob = await axios.get(`/api/investor/downloadDealAgreement?dealId=${order.dealId}`, {
                    responseType: 'blob',
                });
                filename = 'Deal_Agreement'
            }

            if (type === 'note') {
                blob = await axios.get(`/api/investor/downloadTransactionNote?dealId=${order.dealId}&tab=${selectedTab}`, {
                    responseType: 'blob',
                });
                filename = 'TransactionNote'
            }

            if(type === 'invoice' && !isOngoing){
                blob = await axios.get(`/api/investor/downloadTransactionInvoice?dealId=${order.dealId}`, {
                    responseType: 'blob',
                });
                filename = 'TransactionInvoice'
            }

            if(type === 'repay' && !isOngoing){
                blob = await axios.get(`/api/investor/downloadRepaymentStatus?dealId=${order.dealId}`, {
                    responseType: 'blob',
                });
                filename = 'RepaymentStatus'
            }

            if(type === 'all'){
                blob = await axios.get(`/api/investor/downloadAll?dealId=${order.dealId}&tab=${selectedTab}`, {
                    responseType: 'blob',
                });
                filename = 'Documents'
            }

            if(type === 'all' && !isOngoing){
                blob = await axios.get(`/api/investor/downloadAll?dealId=${order.dealId}&type=all&tab=${selectedTab}`, {
                    responseType: 'blob',
                });
                filename = 'Documents'
            }

            await validateDownloadBlob(blob);

            let downloadedFileName;
            if (type === 'all') {
                downloadedFileName = `TransactionDocs_${order.dealId}.zip`;
                downloadBlob(blob, downloadedFileName);
            } else if (blob) {
                downloadedFileName = `${filename}_${order.dealId}.pdf`;
                downloadBlob(blob, downloadedFileName);
            }

            if (downloadedFileName) {
                message.success(getDownloadSuccessMessage(DOWNLOAD_LABELS[type]));
            }

        } catch (err) {
            console.error('Error downloading document:', err);
            message.error(await getDownloadErrorMessage(err, 'Failed to download document. Please try again later.'));
        }
    };

    const items = [
        {
            key: '1',
            label: <span onClick={() => handleDownload('dealAgreement')} className='download-dropdown-item'>Deal Agreement</span>,
        },
        {
            key: '2',
            label: <span onClick={() => handleDownload('note')} className='download-dropdown-item'>Transaction Note</span>,
        },
        {
            key: '3',
            label: <span onClick={() => handleDownload('invoice')} className='download-dropdown-item'>Transaction Invoice</span>,
            disabled: isOngoing,
        },
        {
            key: '4',
            label: <span onClick={() => handleDownload('repay')} className='download-dropdown-item'>Repayment Status</span>,
            disabled: isOngoing,
        },
        {
            key: '5',
            label: <span onClick={() => { handleDownload('all')}} className='download-dropdown-item'>Download All</span>,
        },
    ];

    const returns = calculateReturns(order?.investment?.discountRate);


    return (
        <div className='order-card-container'>
            <div className='order-details-container'>
                <Row align="middle" gutter={[16, 16]}>
                    <Col xs={24} lg={6}>
                        <Image src={order?.enterprise?.logo} height={100} width={200} alt='Ent Logo' preview={false} />
                    </Col>
                    <Col xs={24} lg={14}>
                        <Flex vertical gap="small">
                            <Text strong>{order?.enterprise?.name}</Text>
                            <Text style={{ fontSize: 12, color: "#40566D" }}>DEAL ID: {order?.dealId}</Text>
                        </Flex>
                    </Col>
                    <Col xs={0} lg={4}>
                        <div className='download-dropdown-container'>
                            <Dropdown menu={{ items }} trigger={['click']} overlayStyle={{ width: 200 }} placement="bottom">
                                <CustomButton
                                    size="large"
                                    variant="outlined"
                                    icon={<DownOutlined style={{ fontSize: 12 }} />}
                                    iconPosition="end"
                                    className="dropdown-btn"
                                >
                                    Download
                                </CustomButton>
                            </Dropdown>
                        </div>
                    </Col>
                </Row>
                <div className="order-constraint-container">
                    <div className="order-constraint-details">
                        <Text style={{ fontSize: 12, color: "#40566d" }}>Interest Rate</Text>
                        <Text strong style={{ fontSize: 14 }}>{returns}%</Text>
                    </div>
                    <div className="order-constraint-details">
                        <Text style={{ fontSize: 12, color: "#40566d" }}>Amount Invested</Text>
                        <Text strong style={{ fontSize: 14 }}>{formatCurrency(order?.investment?.investedAmt) || "N/A"}</Text>
                    </div>
                    <div className="order-constraint-details">
                        <Text style={{ fontSize: 12, color: "#40566d" }}>{selectedTab === "ongoing" ? "Maturity" : "Matured"} Amount</Text>
                        <Text strong style={{ fontSize: 14 }}>{formatCurrency(isOngoing ? order?.investment?.maturityAmount : order?.investment?.maturedAmt) || "N/A"}</Text>
                    </div>
                    <div className="order-constraint-details">
                        <Text style={{ fontSize: 12, color: "#40566d" }}>{selectedTab === "ongoing" ? "Expected" : "Realized"} Gross Returns</Text>
                        <Text strong style={{ fontSize: 14 }}>{formatCurrency(isOngoing ? order?.investment?.expectedGrossReturns : order?.investment?.realizedGrossReturns) || "N/A"}</Text>
                    </div>
                    <div className="order-constraint-details">
                        <Text style={{ fontSize: 12, color: "#40566d" }}>{selectedTab === "ongoing" ? "Expected" : "Realized"} Net Returns</Text>
                        <Text strong style={{ fontSize: 14 }}>{formatCurrency(isOngoing ? order?.investment?.expectedNetReturns : order?.investment?.realizedNetReturns) || "N/A"}</Text>
                    </div>
                </div>
                <div className='download-dropdown-container-mobile'>
                    <Dropdown menu={{ items }} trigger={['click']} overlayStyle={{ width: 200 }} placement="bottom">
                        <CustomButton
                            size="large"
                            variant="outlined"
                            block
                            icon={<DownOutlined style={{ fontSize: 12 }} />}
                            iconPosition="end"
                            className="dropdown-btn"
                        >
                            Download
                        </CustomButton>
                    </Dropdown>
                </div>
            </div>
            <div className='order-status-container'>
                <HorizotalSteps dealBookedDate={order?.timeline?.bookedDate} dealExecutedDate={order?.timeline?.executedDate} dealDisbursedDate={order?.timeline?.disbursedDate} status={order?.statusName} repaymentDate={order?.timeline?.expectedRepaymentDate} repaidDate={isOngoing ? order?.timeline?.actualRepaymentDate : order?.timeline?.closedDate} />
            </div>
        </div>
    )
}

export default memo(OrderCard);